import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Star, MessageSquarePlus, BadgeCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, readonly = false, size = 20 }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          data-testid={`star-${star}`}
          onClick={() => !readonly && onRatingChange && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          className={readonly ? 'cursor-default' : 'cursor-pointer'}
          disabled={readonly}
        >
          <Star
            size={size}
            className={
              star <= (hoverRating || rating)
                ? 'fill-[#C1A58D] text-[#C1A58D]'
                : 'text-[#E3E5DF]'
            }
          />
        </button>
      ))}
    </div>
  );
};

export const Reviews = ({ roomId }) => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [roomId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/room/${roomId}`);
      setReviews(response.data.reviews);
      setTotal(response.data.total);
      setAvgRating(response.data.average_rating);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guestName || !comment) {
      toast.error(language === 'it' ? 'Compila tutti i campi' : 'Please fill all fields');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, {
        room_id: roomId,
        guest_name: guestName,
        guest_email: guestEmail || null,
        rating,
        comment,
        language
      });

      toast.success(language === 'it' ? 'Recensione inviata!' : 'Review submitted!');
      setGuestName('');
      setGuestEmail('');
      setRating(5);
      setComment('');
      setIsDialogOpen(false);
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 
            className="text-3xl font-light text-[#2C302E]"
            style={{fontFamily: 'Cormorant Garamond, serif'}}
          >
            {language === 'it' ? 'Recensioni' : 'Reviews'}
          </h2>
          {total > 0 && (
            <div className="flex items-center gap-3 mt-2">
              <StarRating rating={Math.round(avgRating)} readonly />
              <span className="text-sm text-[#2C302E]/70">
                {avgRating.toFixed(1)} ({total} {language === 'it' ? 'recensioni' : 'reviews'})
              </span>
            </div>
          )}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-review-button"
              variant="outline"
              className="border-[#8A9A86] text-[#8A9A86] hover:bg-[#8A9A86] hover:text-white rounded-full"
            >
              <MessageSquarePlus size={16} className="mr-2" />
              {language === 'it' ? 'Lascia Recensione' : 'Leave Review'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cormorant Garamond, serif'}} className="text-2xl font-light">
                {language === 'it' ? 'Scrivi una recensione' : 'Write a review'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="review-name">{language === 'it' ? 'Nome' : 'Name'}</Label>
                <Input
                  id="review-name"
                  data-testid="review-name-input"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Mario Rossi"
                  required
                />
              </div>

              <div>
                <Label htmlFor="review-email">
                  {language === 'it' ? 'Email (usa quella della prenotazione per essere verificato)' : 'Email (use your booking email to be verified)'}
                </Label>
                <Input
                  id="review-email"
                  type="email"
                  data-testid="review-email-input"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder={language === 'it' ? 'la-tua-email@esempio.com (opzionale)' : 'your-email@example.com (optional)'}
                />
                <p className="text-xs text-[#2C302E]/50 mt-1">
                  {language === 'it' 
                    ? 'Se hai già soggiornato da noi, verrai marcato come ospite verificato ✓' 
                    : 'If you have already stayed with us, you will be marked as verified guest ✓'}
                </p>
              </div>

              <div>
                <Label>{language === 'it' ? 'Valutazione' : 'Rating'}</Label>
                <div className="mt-2">
                  <StarRating rating={rating} onRatingChange={setRating} size={24} />
                </div>
              </div>

              <div>
                <Label htmlFor="review-comment">{language === 'it' ? 'Commento' : 'Comment'}</Label>
                <Textarea
                  id="review-comment"
                  data-testid="review-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={language === 'it' ? 'La tua esperienza...' : 'Your experience...'}
                  rows={4}
                  required
                />
              </div>

              <div className="flex gap-3 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  {t('admin.cancel')}
                </Button>
                <Button 
                  type="submit"
                  data-testid="submit-review-button"
                  disabled={submitting}
                  className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white"
                >
                  {submitting ? t('booking.processing') : (language === 'it' ? 'Invia' : 'Submit')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-[#2C302E]/60 text-center py-8">{t('common.loading')}</p>
      ) : reviews.length === 0 ? (
        <Card className="p-8 text-center border border-[#E3E5DF]/40">
          <p className="text-[#2C302E]/60">
            {language === 'it' 
              ? 'Nessuna recensione ancora. Sii il primo!' 
              : 'No reviews yet. Be the first!'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card 
              key={review.review_id} 
              className="p-6 border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              data-testid={`review-${review.review_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-[#2C302E]">{review.guest_name}</h4>
                    {review.is_verified && (
                      <span 
                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#8A9A86]/10 text-[#8A9A86] rounded-full font-medium"
                        data-testid={`verified-badge-${review.review_id}`}
                      >
                        <BadgeCheck size={12} strokeWidth={2.5} />
                        {language === 'it' ? 'Ospite Verificato' : 'Verified Guest'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#2C302E]/50 mt-1">
                    {format(new Date(review.created_at), 'dd/MM/yyyy')}
                  </p>
                </div>
                <StarRating rating={review.rating} readonly size={16} />
              </div>
              <p className="text-[#2C302E]/80 leading-relaxed">{review.comment}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
